/**
 * CallCenterTickets - Main Parent Component
 * 
 * Simple tab switcher. All CRUD logic lives inside each tab component.
 */

import React, { useState, useMemo, useEffect, lazy, Suspense } from 'react';
import FusePageSimple from '@fuse/core/FusePageSimple';
import FuseAnimate from '@fuse/core/FuseAnimate/FuseAnimate';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, CircularProgress } from '@material-ui/core';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LabelIcon from '@mui/icons-material/Label';

// Lazy load tab components
const CallCenterTicketsReportsTab = lazy(() => import('./tabs/CallCenterTicketsReportsTab'));
const CallCenterTicketsStatusesTab = lazy(() => import('./tabs/CallCenterTicketsStatusesTab'));

const useStyles = makeStyles(theme => ({
	root: {
		width: '100%'
	},
	contentContainer: {
		padding: theme.spacing(2)
	},
	tabsContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: 0,
		backgroundColor: theme.palette.type === 'dark' ? '#1F2937' : '#F3F4F6',
		borderRadius: 12,
		padding: 4,
		marginBottom: theme.spacing(3),
		width: 'fit-content',
		flexWrap: 'wrap'
	},
	tab: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		padding: theme.spacing(1.25, 3),
		borderRadius: 10,
		cursor: 'pointer',
		fontSize: 14,
		fontWeight: 500,
		color: theme.palette.text.secondary,
		backgroundColor: 'transparent',
		border: 'none',
		transition: 'all 0.2s ease',
		'&:hover': {
			backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'
		}
	},
	activeTab: {
		backgroundColor: theme.palette.type === 'dark' ? '#374151' : '#FFFFFF',
		color: theme.palette.type === 'dark' ? '#FFFFFF' : theme.palette.primary.main,
		boxShadow: theme.palette.type === 'dark'
			? '0 2px 8px rgba(0,0,0,0.3)'
			: '0 2px 8px rgba(0,0,0,0.08)',
		fontWeight: 600
	},
	tabIcon: {
		fontSize: 20
	},
	tabContent: {
		minHeight: 400
	},
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: 400
	},
	headerWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
		padding: theme.spacing(0, 2)
	},
	headerLeft: {
		display: 'flex',
		alignItems: 'center'
	},
	headerIcon: {
		marginRight: theme.spacing(1.5)
	},
	headerTitle: {
		fontSize: '18px',
		fontWeight: 600,
		color: theme.palette.text.primary
	}
}));

const TABS = [
	{ id: 'tickets-report', label: 'Tickets Report', icon: AssignmentIcon, component: CallCenterTicketsReportsTab },
	{
		id: 'tickets-statuses',
		label: 'Tickets Statuses',
		icon: LabelIcon,
		component: CallCenterTicketsStatusesTab,
		permission: 'FRONT:/apps/callcenter/tickets/statuses'
	}
];

function CallCenterTickets() {
	const classes = useStyles();
	const permissions = JSON.parse(localStorage.getItem('user_acl') || '{}');

	const visibleTabs = useMemo(
		() => TABS.filter(tab => !tab.permission || permissions[tab.permission] == 1),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const [activeTab, setActiveTab] = useState(visibleTabs[0]?.id);

	useEffect(() => {
		if (!visibleTabs.some(tab => tab.id === activeTab)) {
			setActiveTab(visibleTabs[0]?.id);
		}
	}, [visibleTabs, activeTab]);

	const renderTabContent = () => {
		const currentTab = visibleTabs.find(tab => tab.id === activeTab);
		if (!currentTab) return null;
		const TabComponent = currentTab.component;

		return (
			<Suspense
				fallback={
					<div className={classes.loadingContainer}>
						<CircularProgress />
					</div>
				}
			>
				<TabComponent />
			</Suspense>
		);
	};

	return (
		<FusePageSimple
			header={
				<div className={classes.headerWrapper}>
					<div className={classes.headerLeft}>
						<FuseAnimate animation="transition.expandIn" delay={300}>
							<ConfirmationNumberIcon fontSize="large" className={classes.headerIcon} />
						</FuseAnimate>
						<FuseAnimate animation="transition.slideLeftIn" delay={300}>
							<Typography variant="h6" className={classes.headerTitle}>
								Call Center Tickets
							</Typography>
						</FuseAnimate>
					</div>
				</div>
			}
			content={
				<div className={classes.contentContainer}>
					{visibleTabs.length > 1 && (
						<div className={classes.tabsContainer}>
							{visibleTabs.map((tab) => {
								const IconComponent = tab.icon;
								return (
									<button
										key={tab.id}
										className={`${classes.tab} ${activeTab === tab.id ? classes.activeTab : ''}`}
										onClick={() => setActiveTab(tab.id)}
									>
										<IconComponent className={classes.tabIcon} />
										{tab.label}
									</button>
								);
							})}
						</div>
					)}

					<div className={classes.tabContent}>
						{renderTabContent()}
					</div>
				</div>
			}
		/>
	);
}

export default CallCenterTickets;